import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { resellerAPI } from '../../services/api';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import { Download, Database, History as HistoryIcon } from 'lucide-react';

const ResellerSalesHistory = () => {
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({ total_orders: 0, total_coins: 0, total_amount: 0, deleted_count: 0 });
  const [filters, setFilters] = useState({
    search: '',
    start_date: '',
    end_date: '',
    include_deleted: true
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      // Decide entre KAKO ID (numérico) ou nickname (texto)
      const searchInput = (filters.search || '').trim();
      if (searchInput) {
        if (/^\d+$/.test(searchInput)) {
          params.reseller_kako_id = searchInput;
        } else {
          params.reseller_nickname = searchInput;
        }
      }
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      params.include_deleted = filters.include_deleted;
      params.limit = 1000;
      
      const res = await resellerAPI.getSalesHistory(params);
      setSales(res.data?.sales || []);
      setSummary(res.data?.summary || { total_orders: 0, total_coins: 0, total_amount: 0, deleted_count: 0 });
    } catch (e) {
      toast.error('Erro ao carregar histórico de vendas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const runMigration = async () => {
    if (!window.confirm('⚙️ MIGRAÇÃO INICIAL\n\nIsso vai copiar todos os pedidos completed (com revendedor) da coleção `orders` para `reseller_sales`.\n\nÉ idempotente: pode rodar várias vezes sem duplicar.\n\nContinuar?')) return;
    setMigrating(true);
    try {
      const res = await resellerAPI.migrateSalesFromOrders();
      toast.success(`Migração: ${res.data.inserted} inseridas, ${res.data.already_existed} já existiam`);
      load();
    } catch (e) {
      toast.error('Erro na migração');
    } finally {
      setMigrating(false);
    }
  };

  const exportCSV = () => {
    if (sales.length === 0) {
      toast.error('Nada para exportar');
      return;
    }
    const headers = ['data', 'order_id', 'reseller_kako_id', 'reseller_nickname', 'buyer_kako_id', 'buyer_nickname', 'coins', 'amount_brl', 'gateway', 'order_deleted'];
    const rows = sales.map(s => [
      s.completed_at,
      s.order_id || '',
      s.reseller_kako_id || '',
      (s.reseller_nickname || '').replace(/[,;\n]/g, ' '),
      s.buyer_kako_id || '',
      (s.buyer_nickname || '').replace(/[,;\n]/g, ' '),
      s.coins,
      s.amount,
      s.gateway || '',
      s.order_deleted ? 'sim' : 'nao'
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendas-afiliados-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <HistoryIcon className="w-5 h-5 text-green-600" />
            Histórico Imutável de Vendas dos Afiliados
          </CardTitle>
          <p className="text-xs sm:text-sm text-gray-500">
            Esta coleção é independente de <code className="bg-gray-100 px-1 rounded">orders</code>. Vendas permanecem aqui mesmo após admin apagar pedidos.
            As tarefas mensais continuam resetando normalmente.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Botão de migração (uso único após deploy) */}
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-amber-800 flex items-center gap-1">
                <Database className="w-4 h-4" /> Migração Inicial (uso único)
              </p>
              <p className="text-[10px] sm:text-xs text-amber-700">
                Importa todos os pedidos completed com revendedor existentes para o novo histórico imutável. Idempotente.
              </p>
            </div>
            <Button
              variant="outline"
              className="bg-white text-amber-700 border-amber-400 h-9 text-xs sm:text-sm shrink-0"
              onClick={runMigration}
              disabled={migrating}
              data-testid="run-migration-btn"
            >
              {migrating ? 'Migrando...' : 'Rodar Migração'}
            </Button>
          </div>

          {/* Filtros */}
          <div className="grid gap-3 md:grid-cols-5">
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">KAKO ID ou Nome do Afiliado</Label>
              <Input
                placeholder="ex: 10376644 ou MAX"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                data-testid="filter-reseller-id"
              />
              <p className="text-[10px] text-gray-400">Digite o KAKO ID (números) ou nome (busca parcial)</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data Início</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data Fim</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={filters.include_deleted}
                  onChange={(e) => setFilters({ ...filters, include_deleted: e.target.checked })}
                />
                Incluir pedidos apagados
              </Label>
              <div className="flex gap-2">
                <Button onClick={load} disabled={loading} className="flex-1 h-9 text-xs bg-green-600 hover:bg-green-700" data-testid="apply-filter-btn">
                  {loading ? 'Buscando...' : 'Aplicar'}
                </Button>
                <Button variant="outline" onClick={exportCSV} className="h-9 text-xs" data-testid="export-csv-btn">
                  <Download className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-[10px] sm:text-xs text-blue-600">Total de vendas</p>
              <p className="text-lg sm:text-xl font-bold text-blue-800">{summary.total_orders.toLocaleString('pt-BR')}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-[10px] sm:text-xs text-green-600">Moedas vendidas</p>
              <p className="text-lg sm:text-xl font-bold text-green-800">{summary.total_coins.toLocaleString('pt-BR')}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-[10px] sm:text-xs text-purple-600">Faturamento R$</p>
              <p className="text-lg sm:text-xl font-bold text-purple-800">{(summary.total_amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-[10px] sm:text-xs text-red-600">Pedidos apagados (preservados)</p>
              <p className="text-lg sm:text-xl font-bold text-red-800">{summary.deleted_count.toLocaleString('pt-BR')}</p>
            </div>
          </div>

          {/* Tabela */}
          {loading ? (
            <div className="flex justify-center p-8"><FoxSpinnerInline size="md" /></div>
          ) : sales.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Nenhuma venda no filtro</p>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                <span>Mostrando <strong>{sales.length}</strong> venda(s) — role para ver mais</span>
              </div>
              <div className="overflow-auto border rounded-lg max-h-[500px] relative">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-gray-100 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="text-left p-2">Data</th>
                    <th className="text-left p-2">Pedido</th>
                    <th className="text-left p-2">Afiliado</th>
                    <th className="text-left p-2">Comprador</th>
                    <th className="text-right p-2">Moedas</th>
                    <th className="text-right p-2">R$</th>
                    <th className="text-center p-2">Gateway</th>
                    <th className="text-center p-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s) => (
                    <tr key={s.id} className={`border-b hover:bg-gray-50 ${s.order_deleted ? 'bg-red-50' : ''}`} data-testid={`sale-row-${s.id}`}>
                      <td className="p-2 whitespace-nowrap">{s.completed_at ? new Date(s.completed_at).toLocaleString('pt-BR') : '-'}</td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (s.order_id) {
                              navigator.clipboard.writeText(s.order_id);
                              toast.success('ID do pedido copiado');
                            }
                          }}
                          title={s.order_id || ''}
                          className="font-mono text-[11px] text-blue-700 hover:underline cursor-pointer"
                          data-testid={`order-id-${s.id}`}
                        >
                          {s.order_id ? s.order_id.slice(0, 8) : '-'}
                        </button>
                      </td>
                      <td className="p-2">
                        <div className="font-medium">{s.reseller_nickname || '-'}</div>
                        <div className="text-gray-500 text-[10px]">{s.reseller_kako_id}</div>
                      </td>
                      <td className="p-2 text-gray-600">
                        <div className="font-medium text-gray-800">{s.buyer_nickname || '-'}</div>
                        <div className="text-[10px] text-gray-500 font-mono">{s.buyer_kako_id || '-'}</div>
                      </td>
                      <td className="p-2 text-right font-mono font-semibold text-green-700">{(s.coins || 0).toLocaleString('pt-BR')}</td>
                      <td className="p-2 text-right font-mono">{(s.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      <td className="p-2 text-center text-gray-500 text-[10px]">{s.gateway || '-'}</td>
                      <td className="p-2 text-center">
                        {s.order_deleted ? (
                          <Badge variant="outline" className="text-red-700 border-red-300 text-[10px]">Pedido apagado</Badge>
                        ) : (
                          <Badge className="bg-green-600 text-white text-[10px]">OK</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResellerSalesHistory;
